import React, { useEffect, useState } from "react";
import getConfig from "next/config";

import { User, UserLocation } from "@themuse/design-system/lib/main";
import { formatLog } from "utils/logging";
import mockUser from "mocks/mockuser.json";

export interface AppUser extends User {
    isInitialized?: boolean;
}

interface UserProviderProps {
    user?: AppUser;
    userLocation?: UserLocation;
}

const defaultUserState: AppUser = {
    isInitialized: false,
    logged_in: false,
    view_bookings: false
};

const UserContext = React.createContext<AppUser>(defaultUserState);

const UserProvider: React.FC<React.PropsWithChildren<UserProviderProps>> = (props) => {
    const { children, user, userLocation } = props;
    const initialState = user ? {
        ...user,
        location: userLocation
    } : {
        ...defaultUserState,
        location: userLocation
    };
    const [userState, setUserState] = useState(initialState);
    const { publicRuntimeConfig: { museWebHost, appName } } = getConfig();

    useEffect(() => {
        const fetchUser = async () => {
            // the default logged out user.
            const base = { logged_in: false, view_bookings: false } as unknown as AppUser;
            const msg = `${appName} was unable to fetch current user from ${museWebHost}/api/user.`;
            const isLocal = window.location.host.includes("local");
            if (isLocal) {
                setUserState({
                    ...mockUser,
                    location: userState.location,
                    isInitialized: true
                });
                return;
            }

            try {
                const response = await fetch(`${museWebHost}/api/user`, { credentials: "include" });
                // If we are not a 200 we can still get a valid response but it will cause errors.
                if (response.status !== 200) {
                    throw new Error(`${msg} status: ${response.status}`);
                }
                const result = await response.json() as unknown as AppUser;

                setUserState({
                    ...result,
                    location: userState.location,
                    isInitialized: true
                });
            } catch (error) {
                console.error(msg, JSON.stringify(formatLog({ error })));
                setUserState({
                    ...base,
                    location: userState.location,
                    isInitialized: true
                });
            }
        };

        fetchUser();
    }, []);

    return <UserContext.Provider value={{ ...userState, location: userLocation }}>{children}</UserContext.Provider>;
};

const useUserState = () => {
    const context = React.useContext(UserContext);
    if (context === undefined) {
        throw new Error("useUserState must be used within the SearchProvider");
    }
    return context;
};

export { UserProvider, useUserState };
